import React, { useEffect, useState } from 'react';
import config from '../../config';
import CorrectedCallTranscript from '../../components/CorrectedCallTranscript';

function authHeaders() {
  const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
  return { Authorization: `Bearer ${token}` };
}

function formatPrice(price) {
  if (price == null || price === '') return null;
  const n = Number(price);
  if (Number.isNaN(n)) return null;
  return `₹${Number.isInteger(n) ? n : n.toFixed(0)}`;
}

function outcomeStyle(outcome) {
  switch (outcome) {
    case 'accepted':
      return { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0', label: 'Accepted' };
    case 'rejected':
      return { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', label: 'Rejected' };
    case 'deferred':
      return { bg: '#fffbeb', color: '#b45309', border: '#fde68a', label: 'Deferred' };
    case 'wrong_person':
      return { bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe', label: 'Wrong person' };
    case 'failed':
      return { bg: '#f3f4f6', color: '#4b5563', border: '#e5e7eb', label: 'Failed' };
    case 'cancelled':
      return { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca', label: 'Cut' };
    default:
      return { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb', label: outcome || 'Pending' };
  }
}

function CallRow({ call, live, showTranscript, onCut, cutting }) {
  const outcome = outcomeStyle(call?.outcome);
  const price = formatPrice(call?.quoted_price);
  const title = [call?.vehicle_id, call?.driver_name || call?.driver_first_name || call?.phone || 'Driver']
    .filter(Boolean)
    .join(' · ');
  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderLeft: live ? '4px solid #3b82f6' : `4px solid ${outcome.color}`,
        borderRadius: 8,
        padding: '12px 14px',
        background: live ? '#f8fafc' : '#fff',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>
        {title || 'Driver'}
        {live && (
          <span style={{ marginLeft: 8, fontSize: 12, color: '#2563eb', fontWeight: 500 }}>
            {call?.status || 'ringing'}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginTop: 4 }}>
        <div style={{ fontSize: 12, color: '#6b7280' }}>{call?.phone || '—'}</div>
        {live && call?.room_name && (
          <button
            type="button"
            onClick={() => onCut && onCut(call)}
            disabled={cutting}
            style={{
              fontSize: 12,
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #fecaca',
              background: '#fef2f2',
              color: '#b91c1c',
              cursor: cutting ? 'wait' : 'pointer',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {cutting ? 'Cutting…' : 'Cut call'}
          </button>
        )}
      </div>
      {!live && (
        <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: '2px 8px',
              borderRadius: 999,
              background: outcome.bg,
              color: outcome.color,
              border: `1px solid ${outcome.border}`,
            }}
          >
            {outcome.label}
          </span>
          {price && <span style={{ fontSize: 14, fontWeight: 600 }}>{price}</span>}
        </div>
      )}
      {call?.summary_text && (
        <div style={{ fontSize: 13, color: '#374151', marginTop: 8, lineHeight: 1.4 }}>{call.summary_text}</div>
      )}
      {call?.recording_url && (
        <audio src={call.recording_url} controls style={{ width: '100%', maxWidth: 420, height: 32, marginTop: 10 }} />
      )}
      {showTranscript && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: '#4b5563', fontWeight: 600, marginBottom: 4 }}>
            Corrected transcript
          </div>
          <CorrectedCallTranscript transcript={call?.conversation_transcript} />
        </div>
      )}
    </div>
  );
}

function Section({ title, tone, children, empty }) {
  const tones = {
    live: { bg: '#eff6ff', border: '#bfdbfe' },
    queue: { bg: '#fffbeb', border: '#fde68a' },
    done: { bg: '#fff', border: '#e5e7eb' },
  };
  const palette = tones[tone] || tones.done;
  return (
    <div style={{ border: `1px solid ${palette.border}`, background: palette.bg, borderRadius: 10, padding: 12, marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{title}</div>
      {empty || <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>}
    </div>
  );
}

export default function BookingBotCalls({ bookingId, draftId }) {
  const [board, setBoard] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [cuttingRoom, setCuttingRoom] = useState(null);

  useEffect(() => {
    if (bookingId == null && draftId == null) return undefined;
    let cancelled = false;
    const load = async () => {
      const path = draftId != null
        ? `/voice/drafts/${draftId}/board?all_dates=true`
        : `/voice/bookings/${bookingId}/board?all_dates=true`;
      try {
        const res = await fetch(`${config.opsApiBase}${path}`, { headers: authHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Failed to load bot calls');
        if (!cancelled) {
          setBoard(data.board || null);
          setError('');
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load bot calls');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const timer = setInterval(load, 8000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [bookingId, draftId]);

  const cutCall = async (call) => {
    const room = call?.room_name;
    if (!room || cuttingRoom) return;
    const who = call.driver_name || call.phone || 'this driver';
    if (!window.confirm(`Cut the call to ${who}?`)) return;
    setCuttingRoom(room);
    setError('');
    try {
      const res = await fetch(`${config.opsApiBase}/voice/calls/${encodeURIComponent(room)}/hangup`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Could not cut the call');
      setBoard((prev) => (prev ? { ...prev, active: null } : prev));
    } catch (err) {
      setError(err.message || 'Could not cut the call');
    } finally {
      setCuttingRoom(null);
    }
  };

  const active = board?.active ? [board.active] : [];
  const queued = board?.queued || [];
  const completed = board?.completed || [];
  const label = draftId != null ? `Draft #${draftId}` : `Booking #${bookingId}`;

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>{label}</div>
      {error && <p style={{ fontSize: 13, color: '#b91c1c' }}>{error}</p>}
      {loading && !board && <p style={{ fontSize: 13, color: '#6b7280' }}>Loading calls…</p>}
      <Section
        title={`Now calling (${active.length})`}
        tone="live"
        empty={active.length === 0 ? <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>No active call.</p> : null}
      >
        {active.map((call) => (
          <CallRow
            key={call.room_name || 'active'}
            call={call}
            live
            onCut={cutCall}
            cutting={cuttingRoom === call.room_name}
          />
        ))}
      </Section>
      <Section
        title={`Queued (${queued.length})`}
        tone="queue"
        empty={queued.length === 0 ? <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>No drivers waiting.</p> : null}
      >
        {queued.map((call, index) => (
          <CallRow
            key={`q-${call.phone || index}`}
            call={{ ...call, outcome: 'queued', summary_text: `Queued #${call.position || index + 1}` }}
          />
        ))}
      </Section>
      <Section
        title={`Completed (${completed.length})`}
        tone="done"
        empty={completed.length === 0 ? <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>No completed bot calls for this booking.</p> : null}
      >
        {completed.map((call) => (
          <CallRow key={call.room_name || call.phone} call={call} showTranscript />
        ))}
      </Section>
    </div>
  );
}
